import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BookOpen,
  Landmark,
  GitCompareArrows,
  PiggyBank,
  Send,
  FileSignature,
  Building2,
  Clock,
  ShieldCheck,
  Smartphone,
  FileDown,
  ScanLine,
  ArrowRight,
  CheckCircle2,
  Globe,
} from "lucide-react";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import {
  GREEN,
  GREEN_DEEP,
  GOLD,
  CREAM,
  INK,
  GoldFlourishes,
  PublicNavbar,
  PublicFooter,
  CompanyContactList,
  useScrollToHash,
} from "../components/public/PublicChrome";
import { ServiceGrid } from "./public/Servicios";
import { PlanCards } from "./public/Precios";
import { site } from "../content/siteData";

const LOGO = "/brand/aequor-logo.png";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  initial: {},
  whileInView: {},
  viewport: { once: true, margin: "-80px" },
  transition: { staggerChildren: 0.09 },
};

function SectionHeading({ eyebrow, title, accent, children, light = false }) {
  return (
    <motion.div {...fadeUp} className="max-w-2xl mb-14 md:mb-16">
      <p className="text-[12px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: GOLD }}>
        {eyebrow}
      </p>
      <h2 className="font-display text-4xl md:text-5xl leading-[1.05]" style={{ color: light ? "#fff" : GREEN }}>
        {title}
        {accent && <span className="italic" style={{ color: GOLD }}> {accent}</span>}
      </h2>
      {children && (
        <p className="text-[17px] mt-5 leading-relaxed" style={{ color: light ? "rgba(255,255,255,0.7)" : `${INK}99` }}>
          {children}
        </p>
      )}
    </motion.div>
  );
}

// ============================================================================
// HERO
// ============================================================================
function Hero() {
  const { scrollY } = useScroll();
  const markY = useTransform(scrollY, [0, 420], [0, 36]);
  const markOpacity = useTransform(scrollY, [0, 380], [1, 0.55]);

  return (
    <section className="relative min-h-[100svh] flex items-center pt-24 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 -z-10" style={{ backgroundColor: CREAM }} />
      <div
        className="absolute inset-0 -z-10 opacity-90"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 70% 40%, rgba(184,151,95,0.12), transparent 55%), radial-gradient(ellipse 50% 40% at 15% 80%, rgba(26,51,40,0.07), transparent 50%)",
        }}
      />
      <GoldFlourishes />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative z-10"
        >
          <p className="text-[12px] tracking-[0.28em] uppercase font-semibold mb-5" style={{ color: GOLD }}>
            Tecnología · Formación · Estrategia
          </p>

          <h1
            className="font-display text-[52px] sm:text-[68px] lg:text-[80px] leading-[0.92] tracking-[0.04em] uppercase"
            style={{ color: GREEN }}
          >
            Aequor
          </h1>
          <div className="flex items-center gap-3 mt-2 mb-6" style={{ color: GOLD }}>
            <span className="h-px flex-1 max-w-[48px] bg-current" />
            <span className="text-[13px] tracking-[0.4em] uppercase font-semibold">Group</span>
            <span className="h-px flex-1 max-w-[48px] bg-current" />
          </div>

          <p className="text-[18px] sm:text-[20px] leading-relaxed max-w-lg" style={{ color: `${INK}b8` }}>
            ERP contable en la nube y servicios profesionales de consultoría, asesoría, gestión y auditoría
            contable y financiera, para empresas y estudios de Chile, Argentina y Latinoamérica.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-9">
            <Link
              to="/precios"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 text-[15px] font-semibold px-7 py-3.5 rounded-sm transition-colors"
              style={{ backgroundColor: GREEN, color: CREAM }}
            >
              Ver planes y precios
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </Link>
            <Link
              to="/servicios"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 text-[15px] font-medium px-7 py-3.5 rounded-sm border transition-colors hover:bg-white/50"
              style={{ borderColor: `${GOLD}99`, color: GREEN }}
            >
              Servicios profesionales
            </Link>
            <Link
              to="/agendar"
              data-testid="hero-agendar-cta"
              className="inline-flex items-center justify-center gap-2 text-[15px] font-medium px-7 py-3.5 rounded-sm border transition-colors hover:bg-white/50"
              style={{ borderColor: `${GOLD}99`, color: GREEN }}
            >
              Agendar asesoría
            </Link>
          </div>
        </motion.div>

        <motion.div
          style={{ y: markY, opacity: markOpacity }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
          className="lg:col-span-6 relative flex justify-center lg:justify-end"
        >
          <div className="relative w-full max-w-[520px]">
            <div
              className="absolute -inset-8 rounded-full blur-3xl opacity-40"
              style={{ background: `radial-gradient(circle, ${GOLD}55, transparent 65%)` }}
            />
            <img
              src={LOGO}
              alt="Símbolo Aequor Group"
              className="relative w-full drop-shadow-[0_24px_48px_rgba(26,51,40,0.12)]"
              style={{ filter: "none" }}
              data-testid="hero-brand-mark"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// STATS
// ============================================================================
function StatsStrip() {
  const stats = [
    { value: "ERP", label: "contable multi-empresa en la nube" },
    { value: "Consultoría", label: "contable, financiera y tributaria" },
    { value: "Auditoría", label: "revisión y control interno" },
    { value: "LatAm", label: "atención remota en Chile, Argentina y la región" },
  ];
  return (
    <section id="caracteristicas" className="relative overflow-hidden" style={{ backgroundColor: GREEN_DEEP }} data-testid="stats-strip">
      <div
        className="absolute inset-0 opacity-30"
        style={{ background: `radial-gradient(circle at 20% 50%, ${GOLD}33, transparent 45%)` }}
      />
      <motion.div
        {...stagger}
        className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 relative"
      >
        {stats.map((s) => (
          <motion.div key={s.label} variants={fadeUp} className="text-center md:text-left">
            <div className="font-display text-3xl md:text-4xl text-white tracking-wide mb-1.5">{s.value}</div>
            <div className="h-px w-10 mb-2.5 mx-auto md:mx-0" style={{ backgroundColor: GOLD }} />
            <div className="text-white/55 text-[13px] md:text-sm leading-snug">{s.label}</div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// ============================================================================
// MODULES
// ============================================================================
const MODULES = [
  {
    icon: BookOpen,
    title: "Contabilidad completa",
    desc: "Libro Diario, Mayor, Balance General, Balance de 8 columnas y Estado de Resultados. Plan de cuentas listo y reportes exportables.",
    tone: "green",
  },
  {
    icon: ScanLine,
    title: "Libros de compra y venta",
    desc: "Importa el RCV del SII: el sistema lee columnas, crea contactos si faltan y deja las facturas listas para contabilizar.",
    tone: "gold",
  },
  {
    icon: GitCompareArrows,
    title: "Tesorería y conciliación",
    desc: "Cartolas CSV/Excel, conciliación con el libro y varias cuentas bancarias por empresa sin perder el control.",
    tone: "green",
  },
  {
    icon: Building2,
    title: "Multi-empresa",
    desc: "Cambia de RUT en segundos, permisos por módulo y panel por organización — pensado para estudios contables.",
    tone: "gold",
  },
  {
    icon: PiggyBank,
    title: "Honorarios, pagos y remuneraciones",
    desc: "Boletas con retención, pagos a proveedores, caja chica y liquidaciones — cada operación puede generar su asiento.",
    tone: "green",
    icons: [PiggyBank, Send, FileSignature],
  },
  {
    icon: Landmark,
    title: "Normativa chilena",
    desc: "IVA, honorarios, F29 estimado y estructura coherente con la práctica contable chilena del día a día.",
    tone: "gold",
  },
];

function Features() {
  return (
    <section id="modulos" className="py-24 md:py-32 relative" style={{ backgroundColor: CREAM }}>
      <GoldFlourishes className="opacity-60" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        <SectionHeading eyebrow="Plataforma ERP" title="Cada función en su lugar." accent="Sin adivinar.">
          Un ERP contable en la nube, por suscripción mensual, que sigue el flujo real del contador: desde el RCV
          del SII hasta el informe para la gerencia.
        </SectionHeading>

        <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A3328]/10 border border-[#1A3328]/10">
          {MODULES.map((m, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              className="bg-[#F7F3EB] p-7 md:p-8 hover:bg-white transition-colors duration-300 group"
              data-testid={`feature-${idx}`}
            >
              <div
                className="inline-flex h-12 w-12 rounded-full items-center justify-center mb-5"
                style={{ backgroundColor: m.tone === "gold" ? GOLD : GREEN, color: CREAM }}
              >
                {m.icons ? (
                  <div className="flex gap-0.5">
                    {m.icons.map((Ic, i) => (
                      <Ic key={i} className="h-3.5 w-3.5" strokeWidth={1.75} />
                    ))}
                  </div>
                ) : (
                  <m.icon className="h-5 w-5" strokeWidth={1.75} />
                )}
              </div>
              <h3 className="font-display text-2xl mb-2" style={{ color: GREEN }}>{m.title}</h3>
              <p className="text-[15px] leading-relaxed" style={{ color: `${INK}99` }}>{m.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// SERVICIOS PROFESIONALES
// ============================================================================
function ServicesSection() {
  return (
    <section id="servicios" className="py-24 md:py-32 relative bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <SectionHeading eyebrow="Servicios profesionales" title="Más que un software." accent="Un equipo contable.">
          {site.servicios.intro}
        </SectionHeading>
        <motion.div {...fadeUp}>
          <ServiceGrid />
        </motion.div>
        <motion.div {...fadeUp} className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-[14px] max-w-2xl" style={{ color: `${INK}99` }}>
            Modalidades: proyecto con monto cerrado, iguala mensual o bolsa de horas. Cada propuesta se acuerda por
            escrito antes de comenzar.
          </p>
          <Link
            to="/servicios"
            className="inline-flex items-center gap-2 text-[14px] font-semibold shrink-0"
            style={{ color: GOLD }}
          >
            Cómo trabajamos y cómo se cobra <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// CHILE · ARGENTINA · LATAM
// ============================================================================
function LatamSection() {
  return (
    <section id="latam" className="relative overflow-hidden" style={{ backgroundColor: GREEN }}>
      <GoldFlourishes className="opacity-40" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-24 relative grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <div
            className="inline-flex h-12 w-12 rounded-full items-center justify-center mb-5"
            style={{ backgroundColor: GOLD, color: GREEN_DEEP }}
          >
            <Globe className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <p className="text-[12px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: GOLD }}>
            Cobertura
          </p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-white">{site.latam.titulo}</h2>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7 space-y-4">
          {site.latam.parrafos.map((p, i) => (
            <p key={i} className="text-[16px] leading-relaxed text-white/75">{p}</p>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// PRECIOS
// ============================================================================
function PricingSection() {
  return (
    <section id="precios" className="py-24 md:py-32 relative" style={{ backgroundColor: CREAM }}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <SectionHeading eyebrow="Planes" title="Precios claros," accent="sin permanencia.">
          Suscripción {site.precios.periodicidad} a la plataforma en {site.precios.moneda}. Cambia de plan o cancela
          cuando quieras.
        </SectionHeading>
        <motion.div {...fadeUp}>
          <PlanCards compact />
        </motion.div>
        <motion.div {...fadeUp} className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-[13px] max-w-3xl" style={{ color: `${INK}88` }}>{site.precios.notaImpuestos}</p>
          <Link to="/precios" className="inline-flex items-center gap-2 text-[14px] font-semibold shrink-0" style={{ color: GOLD }}>
            Detalle de cada plan <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// BENEFITS
// ============================================================================
const BENEFITS = [
  {
    icon: Clock,
    title: "Horas en minutos",
    desc: "Importa documentos del SII, genera asientos en lote y concilia sin repetir el mismo trabajo empresa por empresa.",
  },
  {
    icon: Building2,
    title: "Muchas empresas, un login",
    desc: "Ideal para estudios: organiza clientes, cambia de empresa al instante y mantén todo separado.",
  },
  {
    icon: ShieldCheck,
    title: "Base contable sólida",
    desc: "Plan de cuentas coherente, IVA crédito/débito y honorarios listos para el trabajo diario.",
  },
  {
    icon: FileDown,
    title: "Información para decidir",
    desc: "Balance, resultados y proyección de impuestos en PDF para mostrar claridad a tus clientes.",
  },
  {
    icon: GitCompareArrows,
    title: "Tesorería bajo control",
    desc: "Sabe qué está conciliado y qué no, con reglas claras entre el banco y tu libro.",
  },
  {
    icon: Smartphone,
    title: "En cualquier pantalla",
    desc: "Consulta saldos y reportes desde notebook o celular, con una interfaz legible y sin saturar.",
  },
];

function Benefits() {
  return (
    <section id="beneficios" className="py-24 md:py-32 relative bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <SectionHeading eyebrow="Beneficios" title="Optimiza hoy." accent="Rentabiliza mañana.">
          Priorizamos lo que un contador usa cada mes, con rutas claras y menos fricción.
        </SectionHeading>
        <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {BENEFITS.map((b, idx) => (
            <motion.div key={idx} variants={fadeUp} data-testid={`benefit-${idx}`}>
              <div
                className="h-11 w-11 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: idx % 2 === 0 ? GREEN : GOLD, color: CREAM }}
              >
                <b.icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-xl mb-1.5" style={{ color: GREEN }}>{b.title}</h3>
              <p className="text-[14px] leading-relaxed" style={{ color: `${INK}99` }}>{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// CONTACT
// ============================================================================
function Contact() {
  const [form, setForm] = useState({ nombre: "", email: "", empresa: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Gracias! Te contactaremos pronto.");
      setSent(true);
      setForm({ nombre: "", email: "", empresa: "", mensaje: "" });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSending(false);
    }
  };

  const fieldCls =
    "w-full bg-white border border-[#1A3328]/12 rounded-sm px-4 py-3 text-[14px] focus:outline-none focus:border-[#B8975F] focus:ring-1 focus:ring-[#B8975F]/40 transition";
  const labelCls = "text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5 block";

  return (
    <section id="contacto" className="py-24 md:py-32 relative bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div {...fadeUp}>
          <p className="text-[12px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: GOLD }}>
            Contacto
          </p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05]" style={{ color: GREEN }}>
            Conversemos sobre tu empresa
          </h2>
          <p className="text-[17px] mt-5 leading-relaxed" style={{ color: `${INK}99` }}>
            Cuéntanos si necesitas la plataforma, un servicio profesional o ambos. Te respondemos con una propuesta
            adaptada a tu operación.
          </p>
          <CompanyContactList className="mt-10" />
          <p className="text-[13px] mt-6" style={{ color: `${INK}88` }}>{site.empresa.horario}</p>
        </motion.div>

        <motion.form
          {...fadeUp}
          onSubmit={submit}
          className="border border-[#1A3328]/10 rounded-sm p-7 md:p-9"
          style={{ backgroundColor: CREAM }}
          data-testid="contact-form"
        >
          {sent ? (
            <div className="text-center py-12">
              <div className="inline-flex h-14 w-14 rounded-full items-center justify-center mb-4" style={{ backgroundColor: `${GREEN}14` }}>
                <CheckCircle2 className="h-7 w-7" style={{ color: GREEN }} strokeWidth={2} />
              </div>
              <div className="font-display text-2xl mb-2" style={{ color: GREEN }}>¡Mensaje recibido!</div>
              <p style={{ color: `${INK}99` }}>Te contactaremos a la brevedad.</p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 text-[14px] font-medium hover:underline"
                style={{ color: GOLD }}
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className={labelCls} style={{ color: `${INK}99` }}>Nombre</label>
                <input
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="contact-nombre"
                  className={fieldCls}
                  placeholder="Tu nombre"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={{ color: `${INK}99` }}>Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className={fieldCls}
                    placeholder="tu@empresa.com"
                  />
                </div>
                <div>
                  <label className={labelCls} style={{ color: `${INK}99` }}>Empresa</label>
                  <input
                    value={form.empresa}
                    onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                    data-testid="contact-empresa"
                    className={fieldCls}
                    placeholder="(opcional)"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls} style={{ color: `${INK}99` }}>Mensaje</label>
                <textarea
                  required
                  rows={4}
                  value={form.mensaje}
                  onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                  data-testid="contact-mensaje"
                  className={`${fieldCls} resize-none`}
                  placeholder="Cuéntanos brevemente qué necesitas…"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 text-[15px] font-semibold px-6 py-3.5 rounded-sm transition-opacity hover:opacity-90 disabled:opacity-60 mt-2"
                style={{ backgroundColor: GREEN, color: CREAM }}
              >
                {sending ? "Enviando…" : <>Enviar mensaje <ArrowRight className="h-4 w-4" strokeWidth={2} /></>}
              </button>
              <p className="text-[12px] leading-relaxed" style={{ color: `${INK}88` }}>
                Al enviar aceptas que usemos tus datos para responder tu consulta, según nuestra{" "}
                <Link to="/privacidad" className="underline" style={{ color: GREEN }}>Política de privacidad</Link>.
              </p>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

export default function Landing() {
  useScrollToHash();
  useEffect(() => {
    document.title = "Aequor Group | ERP contable y servicios contables";
  }, []);

  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: CREAM, color: INK }} data-testid="landing-page">
      <PublicNavbar />
      <Hero />
      <StatsStrip />
      <Features />
      <ServicesSection />
      <LatamSection />
      <PricingSection />
      <Benefits />
      <Contact />
      <PublicFooter />
    </div>
  );
}
